import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { qk } from '../api/queries'
import type { TriggerResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

const REASON: Record<string, string> = {
  needs_reconnect: 'Needs reconnect',
  delivery_failures: 'Delivery failures',
}

export function TriggersView() {
  const { workspaceId } = useSession()
  const q = useQuery({
    queryKey: qk.triggers(workspaceId),
    queryFn: () => apiFetch<TriggerResponse[]>('/triggers'),
  })
  const triggers = q.data ?? []

  return (
    <div className="page">
      <PageHeader
        title="Triggers"
        count={q.data ? triggers.length : undefined}
        description="Subscriptions that push provider events (webhooks or polling) to your agents."
      />
      <QueryState isLoading={q.isLoading} error={q.error} what="triggers" onRetry={() => q.refetch()}>
        {triggers.length === 0 ? (
          <EmptyState
            icon="bolt"
            title="No triggers yet"
            body="Create a trigger with the SDK or API to have Symone deliver events (new issues, calendar changes, messages) to your agent."
            sampleHeaders={['Provider', 'Mechanism', 'Status', 'Created']}
            sampleRows={[
              ['issues', 'webhook', <Badge tone="success">active</Badge>, 'Oct 2, 2026'],
              ['calendar', 'poll', <Badge>disabled</Badge>, 'Sep 21, 2026'],
            ]}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Mechanism</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>ID</th>
                </tr>
              </thead>
              <tbody>
                {triggers.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-strong">{t.provider}</td>
                    <td>{t.mechanism}</td>
                    <td>
                      <Badge tone={t.status === 'active' ? 'success' : 'neutral'}>{t.status}</Badge>
                      {t.disabled_reason && <div className="cell-sub">{REASON[t.disabled_reason] ?? t.disabled_reason}</div>}
                    </td>
                    <td>{formatDate(t.created_at)}</td>
                    <td><code className="muted">{t.id.slice(0, 8)}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
    </div>
  )
}
